import { NavLink } from 'react-router'
import { ROLE, roleLabel, type Role } from '@ovrsee/shared'
import { useAuth } from '../lib/auth.js'
import { Avatar, Text } from '../ui/index.js'

interface NavItem {
  readonly to: string
  readonly label: string
  readonly roles: readonly Role[]
}

const ITEMS: readonly NavItem[] = [
  { to: '/command', label: 'Command centre', roles: [ROLE.SUPER_MANAGER] },
  { to: '/admin', label: 'Admin', roles: [ROLE.SUPER_MANAGER] },
  { to: '/overview', label: 'Overview', roles: [ROLE.MANAGER, ROLE.SUPER_MANAGER] },
  { to: '/tasks', label: 'Tasks', roles: [ROLE.MANAGER, ROLE.SUPER_MANAGER] },
  { to: '/team', label: 'Team', roles: [ROLE.MANAGER, ROLE.SUPER_MANAGER] },
  { to: '/analytics', label: 'Analytics', roles: [ROLE.MANAGER, ROLE.SUPER_MANAGER] },
  { to: '/my-day', label: 'My day', roles: [ROLE.EMPLOYEE] },
  { to: '/my-progress', label: 'My progress', roles: [ROLE.EMPLOYEE] },
  { to: '/innovation', label: 'Innovation', roles: [ROLE.SUPER_MANAGER, ROLE.MANAGER, ROLE.EMPLOYEE] },
  { to: '/leave', label: 'Leave', roles: [ROLE.MANAGER, ROLE.SUPER_MANAGER, ROLE.EMPLOYEE] },
]

const initials = (name: string) =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]!.toUpperCase())
    .join('')

export function Sidebar() {
  const { user } = useAuth()
  if (user === null) return null

  const items = ITEMS.filter((item) => item.roles.includes(user.role))

  return (
    <nav className="app-sidebar" aria-label="Workspace">
      <div style={{ padding: '0 var(--o-space-5) var(--o-space-5)' }}>
        <span className="wordmark" style={{ fontFamily: 'var(--font-display)', fontSize: 15 }}>
          OVRSEE
        </span>
      </div>

      <div className="card-label" style={{ padding: '0 var(--o-space-5) var(--o-space-2)' }}>
        Workspace
      </div>

      <ul style={{ listStyle: 'none', margin: 0, padding: 0, flex: 1 }}>
        {items.map((item) => (
          <li key={item.to}>
            <NavLink
              to={item.to}
              aria-current={undefined}
              style={({ isActive }) => ({
                display: 'block',
                padding: 'var(--o-space-2) var(--o-space-5)',
                margin: '0 var(--o-space-3)',
                borderRadius: 'var(--r-md)',
                fontSize: 13,
                fontWeight: 500,
                textDecoration: 'none',
                background: isActive ? 'var(--brand-soft)' : 'transparent',
                color: isActive ? 'var(--mauve-700)' : 'var(--fg-2)',
              })}
            >
              {item.label}
            </NavLink>
          </li>
        ))}
      </ul>

      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--o-space-3)',
          padding: 'var(--o-space-4) var(--o-space-5) 0',
          borderTop: '1px solid var(--divider)',
          marginTop: 'var(--o-space-4)',
        }}
      >
        <Avatar text={initials(user.name)} size="small" aria-label={user.name} />
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--fg)' }}>{user.name}</div>
          <Text type="text2" color="secondary">
            {user.jobTitle ?? roleLabel(user.role)}
          </Text>
        </div>
      </div>
    </nav>
  )
}
